import type { ImageSegmenter } from '@mediapipe/tasks-vision';

// Call backgrounds: person segmentation (MediaPipe selfie segmenter, Apache-2.0, served from our own origin
// with its WebAssembly runtime) and the pictures to put behind the person. Everything runs on the device:
// camera frames and your own background picture never leave the browser for this.

export const BACKGROUND_PRESETS = ['office', 'sunset', 'ocean', 'forest', 'bokeh', 'studio'] as const;
export type BackgroundPreset = (typeof BACKGROUND_PRESETS)[number];
export type BackgroundChoice = 'none' | 'blur' | 'blur-strong' | BackgroundPreset | 'custom';

export function backgroundsSupported(): boolean {
  return typeof WebAssembly === 'object' && typeof OffscreenCanvas !== 'undefined' && typeof createImageBitmap === 'function';
}

let segmenter: Promise<ImageSegmenter> | null = null;

/** Loads the model once (about 12 MB runtime + 250 KB model, cached by the browser afterwards). */
export function loadSegmenter(): Promise<ImageSegmenter> {
  segmenter ??= (async () => {
    const { FilesetResolver, ImageSegmenter } = await import('@mediapipe/tasks-vision');
    const fileset = await FilesetResolver.forVisionTasks('/mediapipe');
    const options = (delegate: 'GPU' | 'CPU') => ({
      baseOptions: { modelAssetPath: '/models/selfie_segmenter.tflite', delegate },
      runningMode: 'VIDEO' as const,
      outputCategoryMask: false,
      outputConfidenceMasks: true,
    });
    // The GPU path is much faster; some devices refuse it, so the CPU one is the fallback.
    try {
      return await ImageSegmenter.createFromOptions(fileset, options('GPU'));
    } catch {
      return ImageSegmenter.createFromOptions(fileset, options('CPU'));
    }
  })();
  segmenter.catch(() => {
    segmenter = null; // a later attempt may succeed (e.g. after a network hiccup)
  });
  return segmenter;
}

const W = 1280;
const H = 720;

/** Preset pictures are drawn in code: no image files, crisp at any size. */
export async function presetBackground(preset: BackgroundPreset): Promise<ImageBitmap> {
  const canvas = new OffscreenCanvas(W, H);
  const g = canvas.getContext('2d')!;
  const gradient = (stops: [number, string][], x1 = 0, y1 = 0, x2 = 0, y2 = H) => {
    const lg = g.createLinearGradient(x1, y1, x2, y2);
    for (const [o, c] of stops) lg.addColorStop(o, c);
    g.fillStyle = lg;
    g.fillRect(0, 0, W, H);
  };
  // Small deterministic random so a preset always looks the same.
  let seed = BACKGROUND_PRESETS.indexOf(preset) + 7;
  const rnd = () => ((seed = (seed * 16807) % 2147483647) / 2147483647);

  switch (preset) {
    case 'office': {
      gradient([[0, '#e8edf3'], [1, '#cfd8e3']]);
      // Window panes and a shelf line, softly out of focus.
      g.filter = 'blur(6px)';
      g.fillStyle = 'rgba(255,255,255,0.75)';
      for (let i = 0; i < 3; i++) g.fillRect(140 + i * 230, 90, 200, 300);
      g.fillStyle = '#9aa9bb';
      g.fillRect(0, 470, W, 18);
      g.fillStyle = '#6b8f71';
      g.beginPath();
      g.ellipse(1060, 420, 70, 110, 0, 0, Math.PI * 2);
      g.fill();
      break;
    }
    case 'sunset':
      gradient([[0, '#2b1055'], [0.45, '#d16ba5'], [0.75, '#f9a34a'], [1, '#ffe29a']]);
      g.fillStyle = 'rgba(255,240,200,0.9)';
      g.filter = 'blur(2px)';
      g.beginPath();
      g.arc(W / 2, H * 0.72, 90, 0, Math.PI * 2);
      g.fill();
      break;
    case 'ocean':
      gradient([[0, '#89f7fe'], [0.55, '#66a6ff'], [1, '#1e3c72']]);
      g.strokeStyle = 'rgba(255,255,255,0.25)';
      g.lineWidth = 3;
      for (let y = 380; y < H; y += 36) {
        g.beginPath();
        for (let x = 0; x <= W; x += 20) g.lineTo(x, y + Math.sin(x / 60 + y) * 6);
        g.stroke();
      }
      break;
    case 'forest':
      gradient([[0, '#d4fc79'], [0.5, '#96e6a1'], [1, '#2f6d4f']]);
      g.filter = 'blur(4px)';
      for (let i = 0; i < 18; i++) {
        g.fillStyle = `rgba(${30 + rnd() * 40}, ${90 + rnd() * 60}, ${50 + rnd() * 30}, 0.8)`;
        const x = rnd() * W;
        g.beginPath();
        g.moveTo(x, 160 + rnd() * 120);
        g.lineTo(x - 90, H);
        g.lineTo(x + 90, H);
        g.fill();
      }
      break;
    case 'bokeh':
      gradient([[0, '#141e30'], [1, '#243b55']]);
      for (let i = 0; i < 40; i++) {
        const r = 20 + rnd() * 70;
        g.fillStyle = `hsla(${30 + rnd() * 40}, 90%, ${55 + rnd() * 20}%, ${0.12 + rnd() * 0.25})`;
        g.filter = `blur(${2 + rnd() * 6}px)`;
        g.beginPath();
        g.arc(rnd() * W, rnd() * H, r, 0, Math.PI * 2);
        g.fill();
      }
      break;
    case 'studio': {
      const rg = g.createRadialGradient(W / 2, H * 0.45, 40, W / 2, H * 0.5, W * 0.75);
      rg.addColorStop(0, '#5b6b7f');
      rg.addColorStop(1, '#1b2330');
      g.fillStyle = rg;
      g.fillRect(0, 0, W, H);
      break;
    }
  }
  return canvas.transferToImageBitmap();
}

// ---------- your own picture (kept on this device only) ----------

const DB = 'dijitalapp-call';
const STORE = 'backgrounds';
const KEY = 'custom';

function db(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function tx<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const d = await db();
  return new Promise<T>((resolve, reject) => {
    const req = fn(d.transaction(STORE, mode).objectStore(STORE));
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  }).finally(() => d.close());
}

/** Scales a picked image to at most 1280×720 (cover) and remembers it on this device. */
export async function saveCustomBackground(file: Blob): Promise<ImageBitmap> {
  const source = await createImageBitmap(file);
  const scale = Math.max(W / source.width, H / source.height);
  const canvas = new OffscreenCanvas(W, H);
  const g = canvas.getContext('2d')!;
  const w = source.width * scale;
  const h = source.height * scale;
  g.drawImage(source, (W - w) / 2, (H - h) / 2, w, h);
  source.close();
  const blob = await canvas.convertToBlob({ type: 'image/jpeg', quality: 0.88 });
  await tx('readwrite', (s) => s.put(blob, KEY)).catch(() => undefined);
  return createImageBitmap(blob);
}

export async function loadCustomBackground(): Promise<ImageBitmap | null> {
  try {
    const blob = await tx<Blob | undefined>('readonly', (s) => s.get(KEY) as IDBRequest<Blob | undefined>);
    return blob ? await createImageBitmap(blob) : null;
  } catch {
    return null;
  }
}

export async function removeCustomBackground(): Promise<void> {
  await tx('readwrite', (s) => s.delete(KEY)).catch(() => undefined);
}
